// src/pages/PrivacyPage.jsx
import React from 'react';
import { Shield } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
        <div className="flex items-center space-x-3 mb-8">
          <Shield className="w-10 h-10 text-green-600" />
          <h1 className="text-4xl font-bold text-gray-900">Privacy Policy</h1>
        </div>

        <div className="prose prose-lg max-w-none space-y-8">
          <p className="text-gray-600 text-lg">
            <strong>Last Updated:</strong> June 5, 2026
          </p>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              Welcome to NutriLens Pro. We respect your privacy and are committed to protecting your personal data.
              This privacy policy explains how we collect, use, and safeguard your information when you use our mobile application.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">2.1 Information You Provide</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Product scan history (stored locally on your device)</li>
                  <li>Favorite products (stored locally on your device)</li>
                  <li>Daily nutrition tracking data (stored locally on your device)</li>
                  <li>Allergen and dietary preferences (stored locally on your device)</li>
                  <li>App preferences and settings</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">2.2 Automatically Collected Information</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Camera access for barcode scanning</li>
                  <li>App usage analytics (anonymous, via Firebase Analytics)</li>
                  <li>Crash reports and performance data (via Firebase Crashlytics)</li>
                  <li>Push notification tokens (via Firebase Cloud Messaging)</li>
                  <li>Advertising identifiers for ad personalization (via Google AdMob)</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>To provide barcode scanning and product search functionality</li>
              <li>To retrieve product information from the Open Food Facts database</li>
              <li>To maintain your scan history, favorites, and nutrition logs locally</li>
              <li>To send daily meal reminder notifications (if enabled by you)</li>
              <li>To improve app performance, stability, and user experience</li>
              <li>To display relevant advertisements</li>
              <li>To process optional in-app purchases</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Storage</h2>
            <p className="text-gray-700 leading-relaxed">
              All your personal data — including scan history, favorites, nutrition tracking logs, and preferences — is
              stored locally on your device using Hive database. We do not upload or store this data on our servers.
              Your data remains on your device and is not shared with third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Third-Party Services</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              NutriLens Pro integrates the following third-party services, each of which may collect data according to their own privacy policies:
            </p>
            <div className="space-y-3">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">5.1 Open Food Facts</h3>
                <p className="text-gray-700 leading-relaxed">
                  We use the Open Food Facts API to retrieve product information when you scan a barcode or search for a product.
                  Your search queries and scanned barcodes are sent to their servers. Review their privacy policy at{' '}
                  <a href="https://world.openfoodfacts.org/privacy" className="text-green-600 hover:underline">
                    world.openfoodfacts.org/privacy
                  </a>
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">5.2 Google AdMob</h3>
                <p className="text-gray-700 leading-relaxed">
                  We use Google AdMob to display advertisements. AdMob may collect device identifiers,
                  advertising IDs, and usage data for ad personalization. Review{' '}
                  <a href="https://policies.google.com/privacy" className="text-green-600 hover:underline">
                    Google's privacy policy
                  </a>{' '}
                  for more information.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">5.3 Firebase (Google)</h3>
                <p className="text-gray-700 leading-relaxed">
                  We use Firebase services for app functionality and improvement:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-1 mt-2">
                  <li><strong>Firebase Analytics</strong> — collects anonymous app usage data to help us understand how users interact with the app</li>
                  <li><strong>Firebase Crashlytics</strong> — collects crash reports and diagnostic data to help us fix bugs and improve stability</li>
                  <li><strong>Firebase Cloud Messaging (FCM)</strong> — enables push notifications. We store a device token to deliver notifications; no personal data is sent</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">5.4 RevenueCat</h3>
                <p className="text-gray-700 leading-relaxed">
                  We use RevenueCat to manage optional in-app purchases and donations. RevenueCat may collect anonymous
                  transaction data and device identifiers. Review their privacy policy at{' '}
                  <a href="https://www.revenuecat.com/privacy" className="text-green-600 hover:underline">
                    revenuecat.com/privacy
                  </a>
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Camera Permissions</h2>
            <p className="text-gray-700 leading-relaxed">
              NutriLens Pro requires camera access to scan product barcodes. The camera is only used for scanning
              and no images are stored or transmitted. You can revoke camera permissions at any time through your
              device settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Push Notifications</h2>
            <p className="text-gray-700 leading-relaxed">
              NutriLens Pro may send local push notifications for daily meal reminders (breakfast, lunch, dinner, and hydration).
              These notifications are scheduled locally on your device and do not transmit any data. You can enable or disable
              meal reminders in the app settings, or revoke notification permissions through your device settings at any time.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Our app is not intended for children under 13 years of age. We do not knowingly collect personal
              information from children under 13. If you are a parent or guardian and believe your child has
              provided us with personal information, please contact us.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Your Rights</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Access and review your locally stored data</li>
              <li>Delete your scan history, favorites, and nutrition logs at any time</li>
              <li>Enable or disable meal reminder notifications</li>
              <li>Opt-out of personalized advertisements</li>
              <li>Request information about data we collect</li>
              <li>Delete all app data by uninstalling the app</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Data Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We implement appropriate technical and organizational measures to protect your data. However, no method
              of electronic storage is 100% secure. Since most data is stored locally on your device, you are
              responsible for keeping your device secure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this privacy policy from time to time. We will notify you of any changes by posting the
              new policy in the app and updating the "Last Updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <div className="bg-green-50 p-4 rounded-lg mt-3">
              <p className="text-gray-800">
                <strong>Email:</strong> <a href="mailto:cyborguser2020@gmail.com" className="text-green-600 hover:underline">
                  cyborguser2020@gmail.com
                </a>
              </p>
              <p className="text-gray-800 mt-2">
                <strong>App Name:</strong> NutriLens Pro
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
